import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/components/AuthProvider';
import { GoogleAuthProvider, signInWithPopup, createUserWithEmailAndPassword } from 'firebase/auth';
import { auth, db } from '@/lib/firebase';
import { doc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { AlertCircle, Mail, AlertTriangle } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { showNotification } from '@/lib/generalNotifications';
import { createCompany, DEFAULT_WIKIPAYIT_SETTINGS } from '@/services/companyService';
import axios from 'axios';
import { Separator } from '@/components/ui/separator';
import { runFirestorePermissionTests } from '@/utils/firebaseRulesTester';
import { createDefaultRoles } from '@/lib/api';

// Add custom styles to the top of the file
const preventPointerEventStyles = `
  /* Custom styles to fix input click issues */
  .signup-form-container {
    position: relative;
    z-index: 1;
  }
  
  .signup-input {
    position: relative;
    z-index: 5;
  }
  
  .signup-input:focus {
    outline: 2px solid #4f46e5;
    outline-offset: 1px;
  }
`;

export default function FreeTrialSignup() {
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | React.ReactNode | null>(null);
  const [step, setStep] = useState<'signup' | 'info'>('signup');
  
  // Form data for the user and company
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    companyName: ''
  });
  
  // Component lifecycle debugging
  useEffect(() => {
    
    // Focus the email input on mount after a short delay
    const timer = setTimeout(() => {
      const emailInput = document.getElementById('email');
      if (emailInput) {
        emailInput.focus();
      }
    }, 100);
    
    // Cleanup function
    return () => {
      clearTimeout(timer);
    };
  }, []);
  
  // Log when the step changes
  useEffect(() => {
  }, [step]);
  
  // For validation
  const [validationErrors, setValidationErrors] = useState<{
    email?: string;
    password?: string;
    confirmPassword?: string;
    firstName?: string;
    lastName?: string;
    companyName?: string;
  }>({});

  // Validate form inputs
  const validateForm = () => {
    const errors: typeof validationErrors = {};
    
    if (!formData.email) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }
    
    if (step === 'signup' && !formData.password) {
      errors.password = 'Password is required';
    } else if (step === 'signup' && formData.password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    }
    
    if (step === 'signup' && formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    
    if (step === 'info') {
      if (!formData.firstName) {
        errors.firstName = 'First name is required';
      }
      
      if (!formData.lastName) {
        errors.lastName = 'Last name is required';
      }
      
      if (!formData.companyName) {
        errors.companyName = 'Company name is required';
      }
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle input change with improved error handling
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    // Log input change for debugging
    
    // Prevent event issues by adding explicit focus handling
    if (name === 'email') {
      const emailInput = document.getElementById('email');
      if (emailInput && document.activeElement !== emailInput) {
        emailInput.focus();
      }
    }
    
    // Update form data
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear validation errors for this field
    if (validationErrors[name as keyof typeof validationErrors]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  // Add helper for redirecting to login
  const redirectToLogin = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate('/login');
  };

  // Handle email/password signup - completely rewritten for reliability
  const handleEmailSignup = async (e: React.FormEvent) => {
    // Prevent default form submission
    e.preventDefault();
    e.stopPropagation();
    
    // Clear any existing errors
    setError(null);
    
    
    // Validate form fields
    if (!validateForm()) {
      return;
    }
    
    try {
      // Show processing state
      setIsProcessing(true);
      
      try {
        // Create user in Firebase Authentication
        const userCredential = await createUserWithEmailAndPassword(
          auth, 
          formData.email.trim(), 
          formData.password
        );
        const user = userCredential.user;
        
        // Test Firestore permissions
        const permissionTestResults = await runFirestorePermissionTests();
        
        if (!permissionTestResults.companyTest.success || !permissionTestResults.userTest.success) {
          console.error('Permission tests failed', permissionTestResults);
          setError(
            <div>
              <p>We encountered a permissions issue with your account. This may be due to Firebase security rules.</p>
              <p>Company creation: {permissionTestResults.companyTest.success ? 'Success' : 'Failed'}</p>
              <p>User profile creation: {permissionTestResults.userTest.success ? 'Success' : 'Failed'}</p>
              <p>Please try again or contact support if the issue persists.</p>
            </div>
          );
          setIsProcessing(false);
          return;
        }
        
        // Move to the information collection step
        setStep('info');
      } catch (authError: any) {
        console.error('Firebase auth error:', authError);
        
        // Handle specific Firebase Auth errors
        if (authError.code === 'auth/email-already-in-use') {
          setError(
            <div>
              This email is already registered. You can <button 
                onClick={redirectToLogin} 
                className="text-primary underline font-medium"
                type="button"
              >
                log in here
              </button> or use a different email address.
            </div>
          );
        } else {
          // Map Firebase error codes to user-friendly messages
          const errorMessages: Record<string, string> = {
            'auth/invalid-email': 'Please enter a valid email address.',
            'auth/weak-password': 'Password should be at least 6 characters.',
            'auth/network-request-failed': 'Network error. Please check your internet connection and try again.',
            'auth/too-many-requests': 'Too many attempts. Please try again later.',
            'auth/internal-error': 'An internal error occurred. Please try again.',
          };
          
          setError(errorMessages[authError.code] || authError.message || 'Failed to create account. Please try again.');
        }
      }
    } catch (error: any) {
      // This catch block handles any other errors not related to Firebase Auth
      console.error('Unexpected error during signup:', error);
      setError('An unexpected error occurred. Please try again later.');
    } finally {
      // Always reset the processing state, whether successful or not
      setIsProcessing(false);
    }
  };

  // Handle Google signup
  const handleGoogleSignup = async () => {
    try {
      setIsProcessing(true);
      setError(null);
      
      // Sign in with Google via popup
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      
      // Pre-fill email and names if available from Google account
      setFormData(prev => ({
        ...prev,
        email: user.email || prev.email,
        firstName: user.displayName?.split(' ')[0] || prev.firstName,
        lastName: user.displayName?.split(' ').slice(1).join(' ') || prev.lastName
      }));
      
      // Move to the information collection step
      setStep('info');
      setIsProcessing(false);
      
    } catch (error: any) {
      console.error('Google signup error:', error);
      setError(error.message || 'Failed to sign up with Google. Please try again.');
      setIsProcessing(false);
      
      // Sign out the user if there was any error
      try {
        await auth.signOut();
      } catch (signOutError) {
        console.error('Error signing out after failed Google signup:', signOutError);
      }
    }
  };

  // Complete the signup process with enhanced error handling
  const completeSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setIsProcessing(true);
      setError(null);
      
      const currentUser = auth.currentUser;
      
      if (!currentUser) {
        throw new Error('No authenticated user found');
      }
      
      
      // Create a company ID based on the user ID
      const companyId = `company_${currentUser.uid}`;
      
      try {
        // Create company document via secure service layer
        // This ensures emailSettings.sendgridApiKey is loaded from server environment
        await createCompany({
          companyId,
          userId: currentUser.uid,
          name: formData.companyName,
          email: formData.email,
          address: {
            street: '',
            city: '',
            state: '',
            country: '',
            zip: ''
          },
          phone: '',
          wikiPayitSettings: DEFAULT_WIKIPAYIT_SETTINGS
        });

        // Now update additional company settings via Firestore
        await updateDoc(doc(db, 'companies', companyId), {
          taxId: '',
          logo: '',
          updatedAt: serverTimestamp(),
          ownerId: currentUser.uid,
          platformFee: 0,
          defaultTaxRate: 0,
          enableRecurringInvoices: false,
          enableInstallmentInvoices: false,
          invoiceNumbering: {
            prefix: '',
            nextNumber: 1,
            suffix: '',
            yearInNumber: false,
          },
          // Define default invoice settings
          invoiceSettings: {
            showLogo: false,
            enablePlatformFee: false,
            industry: 'none',
            invoiceType: 'standard',
            paymentMethods: {
              credit_card: true,
              paypal: false,
              ach: false,
              check: false
            },
            invoiceStartingNumber: 1000,
            enableDatePrefix: false,
            invoicePrefix: '',
            showPhoneOnInvoice: false,
            showEmailOnInvoice: false,
            showNotesOnInvoice: true,
            showTermsOnInvoice: true,
            invoicePhone: '',
            invoiceEmail: '',
            colors: {
              primary: '#0D0D0D',  // R13 G13 B13 in hex
              secondary: '#6B7280',
              headerText: '#111827',
              tableHeader: '#F3F4F6',
              tableHeaderText: '#111827',
              totalText: '#111827',
              linkText: '#FFFFFF',
              buttonBackground: '#22C55E'
            },
            template: 'professional',
            numberFormat: 'INV-{NUMBER}',
            zeroPadding: 5,
            resetNumberSequence: 'never',
            dateFormat: 'YYYY-MM-DD',
            customFooterText: "Please enter any text that you would like visible on the invoice",
            defaultDueTerms: 'due_on_receipt',
            defaultNotes: "Please enter notes that are visible on the invoice",
            showUnpaidWatermark: false,
            showPaidWatermark: false
          },
          // Add free trial information
          subscription: {
            plan: 'trial',
            startDate: serverTimestamp(),
            // 30 days from now
            endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
            status: 'active'
          }
        });
      } catch (companyError: any) {
        console.error('Error creating company document:', companyError);
        
        // Check for permission errors
        if (companyError.code === 'permission-denied') {
          setError('Permission denied: You do not have permission to create a company. Please contact support.');
          setIsProcessing(false);
          return;
        }
        
        throw companyError; // Re-throw to be caught by outer catch
      }
      
      try {
        // Create user document
        
        // Set default values for new user
        const userData = {
          uid: currentUser.uid,
          email: formData.email,
          displayName: `${formData.firstName} ${formData.lastName}`.trim(),
          firstName: formData.firstName,
          lastName: formData.lastName,
          photoURL: currentUser.photoURL || '',
          role: 'admin', // Default role for the company owner is always admin
          roleId: null, // We'll try to find a matching role ID later if available
          companyId: companyId,
          status: 'active',
          isActive: true,
          metadata: {},
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          lastLoginAt: serverTimestamp()
        };
        
        // Create the user document
        await setDoc(doc(db, 'users', currentUser.uid), userData);
        
        // Now create default roles for the company including Admin role
        try {
          await createDefaultRoles(companyId, currentUser.uid);
        } catch (rolesError) {
          console.error('Error creating default roles:', rolesError);
          // Continue anyway as the user can still function with the admin role
        }
      } catch (userError: any) {
        console.error('Error creating user document:', userError);
        
        // Check for permission errors
        if (userError.code === 'permission-denied') {
          setError('Permission denied: You do not have permission to create a user profile. Please contact support.');
          setIsProcessing(false);
          return;
        }
        
        throw userError; // Re-throw to be caught by outer catch
      }
      
      showNotification({
        title: 'Success',
        message: 'Your account has been created successfully!',
        type: 'success'
      });
      
      
      // Get current URL parameters
      const urlParams = new URLSearchParams(window.location.search);
      
      // Check if we're in debug mode - make sure we don't redirect to /debug
      const isDebugMode = urlParams.get('debug') === 'true';
      
      // Redirect to onboarding/welcome page using window.location for a full page reload
      // This helps avoid client-side routing issues
      // Make sure we don't carry over any debug parameters
      if (isDebugMode) {
        // Force a clean redirect with no parameters
        window.location.href = '/onboarding';
      } else {
        window.location.href = '/onboarding';
      }
      
    } catch (error: any) {
      console.error('Signup completion error:', error);
      
      // Format the error message for display
      let errorMessage = 'Failed to complete signup. Please try again.';
      
      // Handle specific error types
      if (error.code === 'permission-denied') {
        errorMessage = 'Permission denied: You do not have permission to complete this action.';
      } else if (error.code === 'unavailable') {
        errorMessage = 'Service unavailable: Please check your internet connection and try again.';
      } else if (error.code === 'cancelled') {
        errorMessage = 'Operation cancelled. Please try again.';
      } else if (error.message) {
        errorMessage = `Error: ${error.message}`;
      }
      
      setError(errorMessage);
      setIsProcessing(false);
    }
  };

  // Add debug function
  const debugClick = (e: React.MouseEvent) => {
  };

  // Add email field click debug
  const debugEmailClick = (e: React.MouseEvent<HTMLInputElement>) => {
    e.currentTarget.focus();
  };

  // Add Firebase auth state monitoring
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        // Check if the token is valid
        user.getIdToken(true)
          .then(() => {
            // Token is valid, no action needed
          })
          .catch(error => {
            console.error('Error getting token:', error);
          });
      }
      // No action needed when user is not authenticated
    });
    
    // Cleanup subscription
    return () => unsubscribe();
  }, []);

  // Add a debug button to directly test Firestore permissions
  useEffect(() => {
    // Add a debug button if the URL has ?debug=true
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('debug') === 'true') {
      
      // Add debug button after component mount
      const debugButton = document.createElement('button');
      debugButton.textContent = 'Test Firestore Permissions';
      debugButton.style.position = 'fixed';
      debugButton.style.bottom = '20px';
      debugButton.style.right = '20px';
      debugButton.style.zIndex = '9999';
      debugButton.style.padding = '10px';
      debugButton.style.backgroundColor = '#4f46e5';
      debugButton.style.color = 'white';
      debugButton.style.border = 'none';
      debugButton.style.borderRadius = '5px';
      debugButton.style.cursor = 'pointer';
      
      debugButton.onclick = async () => {
        try {
          const permissionTestResults = await runFirestorePermissionTests();
          
          // Create a result display
          const resultDisplay = document.createElement('div');
          resultDisplay.style.position = 'fixed';
          resultDisplay.style.top = '20px';
          resultDisplay.style.right = '20px';
          resultDisplay.style.padding = '15px';
          resultDisplay.style.backgroundColor = 'white';
          resultDisplay.style.border = '1px solid #ccc';
          resultDisplay.style.borderRadius = '5px';
          resultDisplay.style.zIndex = '10000';
          resultDisplay.style.boxShadow = '0 2px 10px rgba(0,0,0,0.1)';
          resultDisplay.style.maxWidth = '300px';
          
          resultDisplay.innerHTML = `
            <h3 style="margin-top: 0; margin-bottom: 10px;">Permission Test Results</h3>
            <p>Company Test: ${permissionTestResults.companyTest.success ? '✅ Success' : '❌ Failed'}</p>
            <p>User Test: ${permissionTestResults.userTest.success ? '✅ Success' : '❌ Failed'}</p>
            ${!permissionTestResults.companyTest.success || !permissionTestResults.userTest.success 
              ? '<p style="color: red;">Firebase security rules are preventing document creation</p>' 
              : '<p style="color: green;">All permissions tests passed!</p>'}
          `;
          
          // Add close button
          const closeButton = document.createElement('button');
          closeButton.textContent = 'Close';
          closeButton.style.marginTop = '10px';
          closeButton.style.padding = '5px 10px';
          closeButton.style.backgroundColor = '#f0f0f0';
          closeButton.style.border = 'none';
          closeButton.style.borderRadius = '3px';
          closeButton.style.cursor = 'pointer';
          closeButton.onclick = () => {
            document.body.removeChild(resultDisplay);
          };
          
          resultDisplay.appendChild(closeButton);
          document.body.appendChild(resultDisplay);
        } catch (error) {
          console.error('Error testing permissions:', error);
          alert('Error testing permissions. See console for details.');
        }
      };
      
      document.body.appendChild(debugButton);
      
      // Cleanup
      return () => {
        if (document.body.contains(debugButton)) {
          document.body.removeChild(debugButton);
        }
      };
    }
  }, []);

  return (
    <>
      {/* Add style tag to fix pointer events */}
      <style dangerouslySetInnerHTML={{ __html: preventPointerEventStyles }} />
      
      <div className="flex min-h-screen items-center justify-center p-4 bg-gray-50">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-center text-2xl">Start Your Free Trial</CardTitle>
            <CardDescription className="text-center">
              {step === 'signup' 
                ? 'Create your account to get started with your 30-day free trial. No credit card required.'
                : 'Tell us a bit about yourself to complete your registration.'}
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            {error && (
              <Alert variant="destructive" className="mb-4">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Error</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            
            {step === 'signup' ? (
              <>
                <form 
                  onSubmit={handleEmailSignup} 
                  className="space-y-4 signup-form-container" 
                  onClick={debugClick}
                  onFocus={(e) => {
                  }}
                >
                  <div className="space-y-2 relative">
                    <Label htmlFor="email" className="font-medium">Email</Label>
                    <div className="relative">
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        placeholder="your@email.com"
                        value={formData.email}
                        onChange={handleChange}
                        disabled={isProcessing}
                        autoFocus
                        aria-label="Email address for signup"
                        onClick={debugEmailClick}
                        className="signup-input"
                        tabIndex={1}
                        required
                      />
                    </div>
                    {validationErrors.email && (
                      <p className="text-sm text-red-500">{validationErrors.email}</p>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <Input
                      id="password"
                      name="password"
                      type="password"
                      placeholder="••••••••"
                      value={formData.password}
                      onChange={handleChange}
                      disabled={isProcessing}
                    />
                    {validationErrors.password && (
                      <p className="text-sm text-red-500">{validationErrors.password}</p>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Confirm Password</Label>
                    <Input
                      id="confirmPassword"
                      name="confirmPassword"
                      type="password"
                      placeholder="••••••••"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      disabled={isProcessing}
                    />
                    {validationErrors.confirmPassword && (
                      <p className="text-sm text-red-500">{validationErrors.confirmPassword}</p>
                    )}
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="w-full" 
                    disabled={isProcessing}
                  >
                    {isProcessing ? 'Creating Account...' : 'Sign Up with Email'}
                  </Button>
                </form>
                
                <div className="relative my-6">
                  <Separator />
                  <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white px-2 text-xs text-gray-500">
                    OR
                  </span>
                </div>
                
                <Button
                  variant="outline"
                  onClick={handleGoogleSignup}
                  disabled={isProcessing}
                  className="w-full"
                >
                  <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                    <path
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      fill="#4285F4"
                    />
                    <path
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      fill="#34A853"
                    />
                    <path
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      fill="#FBBC05"
                    />
                    <path
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      fill="#EA4335"
                    />
                    <path d="M1 1h22v22H1z" fill="none" />
                  </svg>
                  Sign Up with Google
                </Button>
              </>
            ) : (
              <form onSubmit={completeSignup} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName">First Name</Label>
                  <Input
                    id="firstName"
                    name="firstName"
                    placeholder="John"
                    value={formData.firstName}
                    onChange={handleChange}
                    disabled={isProcessing}
                  />
                  {validationErrors.firstName && (
                    <p className="text-sm text-red-500">{validationErrors.firstName}</p>
                  )}
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="lastName">Last Name</Label>
                  <Input
                    id="lastName"
                    name="lastName"
                    placeholder="Doe"
                    value={formData.lastName}
                    onChange={handleChange}
                    disabled={isProcessing}
                  />
                  {validationErrors.lastName && (
                    <p className="text-sm text-red-500">{validationErrors.lastName}</p>
                  )}
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="companyName">Company Name</Label>
                  <Input
                    id="companyName"
                    name="companyName"
                    placeholder="Acme Inc."
                    value={formData.companyName}
                    onChange={handleChange}
                    disabled={isProcessing}
                  />
                  {validationErrors.companyName && (
                    <p className="text-sm text-red-500">{validationErrors.companyName}</p>
                  )}
                </div>
                
                <Button 
                  type="submit" 
                  className="w-full" 
                  disabled={isProcessing}
                >
                  {isProcessing ? 'Completing Registration...' : 'Complete Registration'}
                </Button>
              </form>
            )}
          </CardContent>
          
          <CardFooter className="flex flex-col space-y-2">
            <div className="text-center text-sm text-gray-500">
              By signing up, you agree to our 
              <a href="#" className="text-primary hover:underline"> Terms of Service</a> and 
              <a href="#" className="text-primary hover:underline"> Privacy Policy</a>.
            </div>
            
            <div className="text-center text-sm">
              Already have an account?{' '}
              <a 
                href="/login" 
                className="text-primary hover:underline"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/login');
                }}
              >
                Log in
              </a>
            </div>
          </CardFooter>
        </Card>
      </div>
    </>
  );
} 